// Modal criar/editar exemplo de redação (com import opcional de PDF).
import { useState } from 'react';
import axios from 'axios';
import {
  Modal, Form, Input, Select, Row, Col, Button, Tag, Upload, Space, Flex, Typography, App as AntApp,
} from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { CATEGORIAS } from './types';
import type { ExemploItem } from './types';

const { Text } = Typography;

interface Props {
  item: ExemploItem | null;
  onClose: () => void;
  onSalvo: () => void;
}

export default function ModalExemplo({ item, onClose, onSalvo }: Props) {
  const { message } = AntApp.useApp();
  const [categoria, setCategoria] = useState(item?.categoria || 'geral');
  const [titulo, setTitulo] = useState(item?.titulo || '');
  const [conteudo, setConteudo] = useState(item?.conteudo || '');
  const [tags, setTags] = useState((item?.tags || []).join(', '));
  const [pdfOrigem, setPdfOrigem] = useState<string | null>((item as any)?.pdf_origem || null);
  const [salvando, setSalvando] = useState(false);
  const [importandoPdf, setImportandoPdf] = useState(false);

  async function importarPdf(file: File) {
    if (!file.name.toLowerCase().endsWith('.pdf')) { message.error('Selecione um .pdf'); return; }
    if (file.size > 7 * 1024 * 1024) { message.error('PDF acima de 7MB.'); return; }
    setImportandoPdf(true);
    try {
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let bin = '';
      const CHUNK = 0x8000;
      for (let i = 0; i < bytes.length; i += CHUNK) {
        bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
      }
      const base64 = btoa(bin);
      const token = await getToken();
      const r = await axios.post(`${API_URL}/agentes/exemplos`,
        { action: 'extrair_pdf', filename: file.name, base64 },
        { headers: { Authorization: token }, timeout: 120000 });
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      const texto = data?.texto || '';
      if (!texto) { message.warning('Não extraiu texto do PDF.'); return; }
      setConteudo(prev => prev?.trim() ? prev + '\n\n--- ' + file.name + ' ---\n\n' + texto : texto);
      setPdfOrigem(file.name);
      if (!titulo.trim()) setTitulo(file.name.replace(/\.pdf$/i, ''));
    } catch (e: any) {
      message.error('Erro PDF: ' + (e.response?.data?.error || e.message));
    } finally { setImportandoPdf(false); }
  }

  async function salvar() {
    if (!titulo.trim() || !conteudo.trim()) { message.warning('Título e conteúdo obrigatórios.'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const body: any = { categoria, titulo, conteudo, tags, pdf_origem: pdfOrigem };
      if (item) {
        await axios.put(`${API_URL}/agentes/exemplos/${item.id}`, body, { headers: { Authorization: token } });
      } else {
        await axios.post(`${API_URL}/agentes/exemplos`, body, { headers: { Authorization: token } });
      }
      message.success('Salvo.');
      onSalvo();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  return (
    <Modal open onCancel={onClose} onOk={salvar} confirmLoading={salvando} destroyOnClose width={760}
      title={item ? 'Editar exemplo' : 'Novo exemplo de redação'} okText="Salvar" cancelText="Cancelar">
      <Form layout="vertical" size="small">
        <Row gutter={10}>
          <Col span={8}>
            <Form.Item label="Categoria" required>
              <Select value={categoria} onChange={setCategoria}
                options={CATEGORIAS.map(c => ({ label: c, value: c }))} />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item label="Título" required>
              <Input value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Ex.: Batimetria Taquari — SEMA 2024" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Tags (separadas por vírgula)">
          <Input value={tags} onChange={e => setTags(e.target.value)} placeholder="Ex.: batimetria, monofeixe, RS" />
        </Form.Item>
        <Form.Item label={
          <Flex justify="space-between" align="center" gap={8} style={{ width: '100%' }}>
            <Text strong style={{ fontSize: 12 }}>Conteúdo da proposta (cole texto ou importe PDF)</Text>
            <Space>
              {pdfOrigem && (
                <Tag closable color="blue" onClose={() => setPdfOrigem(null)}>📎 {pdfOrigem}</Tag>
              )}
              <Upload accept="application/pdf" multiple={false} showUploadList={false}
                beforeUpload={(file) => { importarPdf(file as File); return false; }}>
                <Button size="small" type="primary" ghost icon={<FilePdfOutlined />} loading={importandoPdf}>
                  Importar PDF
                </Button>
              </Upload>
            </Space>
          </Flex>
        } required>
          <Input.TextArea value={conteudo} onChange={e => setConteudo(e.target.value)}
            placeholder="Cole o texto integral... ou clique em 'Importar PDF'."
            autoSize={{ minRows: 12, maxRows: 30 }} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
